import { useEffect, useMemo, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";

import { swSettings } from "app/common/services/swSettings";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import { exportToExcelToastThunk } from "../../table-actions-panel/effects";
import ManualSelectionItem from "../../table-actions-panel/models/selection-dropdown-item/ManualSelectionItem/ManualSelectionItem";
import { exportContactsToExcelService } from "../../table-actions-panel/services/exportExcelService";
import { REVEAL_DROPDOWN_MAX_ITEM, NO_TOUCH_USER_REVEAL_DROPDOWN_MAX_ITEM } from "../constants/revealDropdownItemsList";
import { useContactsFlowStrategy } from "../context/ContactsFlowContext";
import { isRevealable, toUniqueContactId } from "../helpers/common";
import { isExportFlowActive } from "../helpers/exportFlow";
import trackingService from "../services/tracking/trackingService";
import {
    actionsPanelFiltersChangedAction,
    setExportFlowOrigin,
    setExportFlowPhase,
    setRequestedContactsForExport,
    tableRowsSelectionChangedAction,
    tableRowsSelectionToggledAction,
} from "../store/action-creators";
import {
    resetExportFlowStateThunk,
    submitExportThunk,
    confirmRevealAndExportThunk,
    confirmCrmPreviewExportThunk,
    cancelCrmExportThunk,
    cancelCrmLoadingThunk,
} from "../store/export-flow-thunks";
import { selectExportFlowPhase, selectIsExportedExcluded, selectRequestedContactsForExport } from "../store/selectors";
import type { ContactSearchEntry, ExportDestination } from "../types/common";
import type { ExportToExcelFn } from "../types/exportFlow";

import { useExportSelection } from "./useExportSelection";
import useRemainingRevealCredits from "./useRemainingRevealCredits";

export type UseExportPanelFlowParams = {
    contacts: ContactSearchEntry[];
    totalCount: number;
    isLoading: boolean;
    countries: readonly number[];
    onDeactivate: () => void;
    onExportToExcel: ExportToExcelFn;
    onFetchTopNContacts: (n: number) => Promise<ContactSearchEntry[]>;
    onRefetchData?: () => void;
};

export const useExportPanelFlow = ({
    contacts,
    totalCount,
    isLoading,
    countries,
    onDeactivate,
    onExportToExcel,
    onFetchTopNContacts,
    onRefetchData,
}: UseExportPanelFlowParams) => {
    const dispatch = useDispatch();
    const contactsFlowStrategy = useContactsFlowStrategy();
    const isExportedExcluded = useSelector(selectIsExportedExcluded);
    const phase = useSelector(selectExportFlowPhase);
    const requestedContactsForExport = useSelector(selectRequestedContactsForExport);
    const remainingCredits = useRemainingRevealCredits();
    const isNoTouchUser = isNoTouch(swSettings);

    const {
        activeSelectionItem,
        setActiveSelectionItem,
        dropdownItems,
        topNOrPairs,
        manualUnrevealedCount,
        selectedContactsIds,
        exportDestination,
    } = useExportSelection({ contacts, totalCount, isLoading });

    const isActive = isExportFlowActive(phase);
    const isConfirmModalOpen = phase.type === "confirmingReveal";

    const prevPhaseTypeRef = useRef(phase.type);

    useEffect(() => {
        const wasActive = prevPhaseTypeRef.current !== "idle";
        const isNowIdle = phase.type === "idle";
        prevPhaseTypeRef.current = phase.type;

        if (wasActive && isNowIdle) {
            if (isExportedExcluded) {
                dispatch(actionsPanelFiltersChangedAction({ excludeExported: false }));
            }
            onDeactivate();
        }
    }, [phase.type, onDeactivate]);

    const contactsToReveal = useMemo(
        () => requestedContactsForExport.filter((c) => !c.isRevealed && isRevealable(c)),
        [requestedContactsForExport],
    );

    const revealedInSelection = useMemo(
        () => requestedContactsForExport.filter((c) => c.isRevealed),
        [requestedContactsForExport],
    );

    const isSubmitButtonDisabled = useMemo(() => {
        const noResults = contacts.length === 0;
        if (isLoading || isActive || noResults) {
            return true;
        }
        if (activeSelectionItem.id === ManualSelectionItem.ID) {
            return selectedContactsIds.length === 0;
        }
        return false;
    }, [isLoading, isActive, contacts.length, activeSelectionItem.id, selectedContactsIds.length]);

    const clearAndClosePanel = () => {
        if (isExportedExcluded) {
            dispatch(actionsPanelFiltersChangedAction({ excludeExported: false }));
        }
        dispatch(resetExportFlowStateThunk());
        onDeactivate();
    };

    const handleExcludeExportedToggle = () => {
        dispatch(tableRowsSelectionChangedAction([]));
        dispatch(actionsPanelFiltersChangedAction({ excludeExported: !isExportedExcluded, page: 1 }));
    };

    const handleSubmitClick = async () => {
        dispatch(setExportFlowOrigin("panel"));

        const isManualSelection = activeSelectionItem.id === ManualSelectionItem.ID;

        let exportContacts: ContactSearchEntry[];
        let unrevealedCount: number;

        if (isManualSelection) {
            exportContacts = contacts.filter((c) => selectedContactsIds.includes(toUniqueContactId(c)));
            unrevealedCount = manualUnrevealedCount;
        } else {
            const topN = activeSelectionItem.value;
            dispatch(
                setExportFlowPhase({
                    type: "fetchingContacts",
                    variant: exportDestination === "excel" ? "excel" : "crm",
                }),
            );
            try {
                const topNContacts = await onFetchTopNContacts(topN);
                exportContacts = topNContacts;
                unrevealedCount = topNContacts.filter((c) => !c.isRevealed && isRevealable(c)).length;
            } catch {
                dispatch(exportToExcelToastThunk(exportContactsToExcelService.getErrorToastTitle()));
                dispatch(setExportFlowPhase({ type: "idle" }));
                return;
            }
        }

        dispatch(setRequestedContactsForExport(exportContacts));

        if (isManualSelection) {
            dispatch(tableRowsSelectionChangedAction([]));
            dispatch(tableRowsSelectionToggledAction(false));
        }

        if (unrevealedCount > 0) {
            dispatch(
                setExportFlowPhase({
                    type: "confirmingReveal",
                    variant: exportDestination === "excel" ? "excel" : "crm",
                }),
            );
            return;
        }

        await dispatch(submitExportThunk(onExportToExcel, countries, exportContacts));
    };

    const handleConfirmRevealAndExport = async () => {
        const revealMaxItem = isNoTouchUser ? NO_TOUCH_USER_REVEAL_DROPDOWN_MAX_ITEM : REVEAL_DROPDOWN_MAX_ITEM;

        try {
            await dispatch(
                confirmRevealAndExportThunk({
                    exportToExcel: onExportToExcel,
                    fetchTopNContacts: onFetchTopNContacts,
                    revealMultiple: contactsFlowStrategy.dataSource.revealMultiple,
                    countries,
                    remainingCredits,
                    revealedInSelection,
                    contactsToReveal,
                    requestedContactsForExport,
                    revealMaxItem,
                }),
            );
        } finally {
            onRefetchData?.();
        }
    };

    const handleCancelRevealAndExport = () => {
        dispatch(setExportFlowPhase({ type: "idle" }));
        dispatch(setRequestedContactsForExport([]));
    };

    const handleCloseClick = () => {
        trackingService.trackCloseExportPanel(exportDestination as ExportDestination);
        clearAndClosePanel();
    };

    const handleConfirmCrmPreviewExport = () => {
        void dispatch(confirmCrmPreviewExportThunk(countries));
    };

    const handleCancelCrmPreview = () => {
        dispatch(cancelCrmExportThunk());
    };

    const handleCancelCrmLoading = () => {
        dispatch(cancelCrmLoadingThunk());
    };

    return {
        isConfirmModalOpen,
        isExportedExcluded,

        activeSelectionItem,
        dropdownItems,
        selectedContactsIds,
        topNOrPairs,

        contactsToRevealCount: contactsToReveal.length,
        revealedInSelectionCount: revealedInSelection.length,
        remainingCredits,

        isSubmitButtonDisabled,

        clearAndClosePanel,
        onSelectionChange: setActiveSelectionItem,
        onExcludeExportedToggle: handleExcludeExportedToggle,
        onSubmit: handleSubmitClick,
        onConfirmRevealAndExport: handleConfirmRevealAndExport,
        onCancelRevealAndExport: handleCancelRevealAndExport,
        onClose: handleCloseClick,
        onConfirmCrmPreviewExport: handleConfirmCrmPreviewExport,
        onCancelCrmPreview: handleCancelCrmPreview,
        onCancelCrmLoading: handleCancelCrmLoading,
    };
};
